import React, { useEffect, useState } from "react";
import { fetchUomData, createUom, updateUom, deleteUom } from "../api/uomService";
import { UOM } from "../models/uomtypes";
import { Plus, X } from "lucide-react";
import "../styles/ItemModels.css";
import TableComponent from "./TableComponent";
import Pagination from "../view/common/pagination/Pagination";
import '../styles/common/sub-menu.css';
import '../styles/common/spinner.css';
import '../styles/common/icon-style.css'
import "../styles/ItemModels.css";
import '../styles/common/toplayout.css'
import '../styles/common/btn.css'
import '../styles/common/custom-alert.css';
import Select from "react-select";
import CustomAlert from "../view/common/alert/CustomAlert";

import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/slices/store";
import { resetFilters, updateFilters } from "../redux/slices/filterSlice";

const customStyles = `
  .form-group {
    position: relative;
    margin-bottom: 20px;
    width: 100%;
  }
  
  .form-group input {
    width: 100%;
    padding: 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
  }
  
  .form-group input.input-error {
    border-color: #ff6b6b;
  }
  
  .form-error-message {
    color: #ff6b6b;
    font-size: 12px;
    margin-top: 4px;
    position: absolute;
    bottom: -18px;
    left: 0;
  }
  
  .select-container {
    margin-bottom: 20px;
    position: relative;
  }
  
  .select-error .react-select__control {
    border-color: #ff6b6b;
  }
`;

const UOMMaster: React.FC = () => {
  const [uomData, setUomData] = useState<UOM[]>([]);
  const [newUom, setNewUom] = useState<UOM>({ code: "", name: "", status: "ACTIVE" });
  const [editingUom, setEditingUom] = useState<UOM | null>(null);
  const [originalUom, setOriginalUom] = useState<UOM | null>(null); 
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");

  const [validationErrors, setValidationErrors] = useState<{
    code?: string;
    name?: string;
    status?: string;
  }>({});


  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [sortBy, setSortBy] = useState<string>("name");
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertConfirmAction, setAlertConfirmAction] = useState<() => void>(() => {});
  const [alertCancelAction, setAlertCancelAction] = useState<() => void>(() => {});
  const [showConfirmButton, setShowConfirmButton] = useState(true);
  const [showCancelButton, setShowCancelButton] = useState(true);
  const [confirmButtonText, setConfirmButtonText] = useState("OK");
  const [cancelButtonText, setCancelButtonText] = useState("Cancel");
  const [autoCloseDelay, setAutoCloseDelay] = useState<number | null>(null);

  const dispatch = useDispatch();
  const filters = useSelector((state: RootState) => state.filters);
    const  permission = useSelector((state:RootState) => state.permissions)
  
  const [initializeUom, isUomInitialized] = useState(false);
 
  useEffect(()=>{
    dispatch(resetFilters());
    isUomInitialized(true);
  },[dispatch]);
  useEffect(() => {
    if(initializeUom){
    loadUoms();
  }}, [initializeUom,currentPage, itemsPerPage, searchTerm, filters, sortBy, sortOrder]);

  const loadUoms = async () => {
    setIsLoading(true);
    const result = await fetchUomData({ 
      page: currentPage, 
      limit: itemsPerPage, 
      search: searchTerm, 
      filter: filters,
      sortBy: sortBy,
      sortOrder: sortOrder
    });

    if (result.error) {
      setError(result.error);
    } else if (result.data) {
      setUomData(result.data.items ?? []);
      setTotalItems(result.data.total);
      setTotalPages(result.data.totalPages);
    }
    setIsLoading(false);
  };

  const closeAlert = () => {
    setShowAlert(false);
  };

  const configureAlert = (
    message: string, 
    confirmAction: () => void, 
    cancelAction: () => void = () => {}, 
    confirmText: string = "OK", 
    cancelText: string = "Cancel",
    showConfirm: boolean = true,
    showCancel: boolean = true,
    autoClose: number | null = null
  ) => {
    setAlertMessage(message);
    setAlertConfirmAction(() => confirmAction);
    setAlertCancelAction(() => cancelAction);
    setConfirmButtonText(confirmText);
    setCancelButtonText(cancelText);
    setShowConfirmButton(showConfirm);
    setShowCancelButton(showCancel);
    setAutoCloseDelay(autoClose);
    setShowAlert(true);
  };

  const handleSortChange = (sortByField: string, sortOrderDirection: 'asc' | 'desc') => {
    setSortBy(sortByField);
    setSortOrder(sortOrderDirection);
  };

  const hasUomChanged = (): boolean => {
    if (!editingUom || !originalUom) return true;
    
    return (
      editingUom.code !== originalUom.code ||
      editingUom.name !== originalUom.name ||
      editingUom.status !== originalUom.status
    );
  };

  const checkForDuplicates = (): boolean => {
    const existingUoms = editingUom 
      ? uomData.filter(uom => uom._id !== editingUom._id) 
      : uomData;
    
    const currentUom = editingUom || newUom;
    
    const duplicateCode = existingUoms.find(
      uom => uom.code.toLowerCase() === currentUom.code.toLowerCase()
    );
    
    const duplicateName = existingUoms.find(
      uom => uom.name.toLowerCase() === currentUom.name.toLowerCase()
    );
    
    const errors: {code?: string; name?: string} = {...validationErrors};
    
    if (duplicateCode) {
      errors.code = "Code already exists";
    }
    
    if (duplicateName) {
      errors.name = "Name already exists";
    }
    
    setValidationErrors(errors);
    
    if (duplicateCode || duplicateName) {
      configureAlert(
        "Duplicate entry!",
        closeAlert,
        closeAlert,
        "OK",
        "",
        true,
        false,
        3000 
      );
      return true;
    }
    
    return false;
  };

  const validateForm = (): boolean => {
    const errors: {code?: string; name?: string; status?: string} = {};
    const currentUom = editingUom || newUom;
    
    if (!currentUom.code.trim()) {
      errors.code = "Code is required";
    } else if (/\d/.test(currentUom.code)) {
      errors.code = "Numeric characters are not allowed";
    }
    
    if (!currentUom.name.trim()) {
      errors.name = "Name is required";
    } else if (/\d/.test(currentUom.name)) {
      errors.name = "Numeric characters are not allowed";
    }
    
    if (editingUom && !currentUom.status) {
      errors.status = "Status is required";
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddOrEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    
   
    if (!validateForm()) {
      return; 
    }
    
   
    if (checkForDuplicates()) {
      return; 
    }
    
  
    if (editingUom && !hasUomChanged()) {
      setIsModalOpen(false);
      configureAlert(
        "No changes were made.",
        closeAlert,
        closeAlert,
        "OK",
        "",
        true,
        false,
        3000
      );
      return;
    }
    
    setIsLoading(true);
    
    try {
      let updatedUom = editingUom ? { ...editingUom } : { ...newUom };
      
      
      // if (editingUom?.status === "INACTIVE") {
      //   updatedUom.status = "ACTIVE";
      // }

      let result;
      if (editingUom) {
    
        result = await updateUom(editingUom._id!, updatedUom);
        setIsModalOpen(false);
        
  
        configureAlert(
          `${updatedUom.name} has been updated successfully.`,
          () => {
            loadUoms();
            closeAlert();
          },
          closeAlert,
          "OK",
          "",
          true,
          false,
          3000 
        );
      } else {
        result = await createUom(newUom);
        setIsModalOpen(false);
        
        configureAlert(
          `${newUom.name} has been added successfully.`,
          () => {
            loadUoms();
            closeAlert();
          },
          closeAlert,
          "OK",
          "",
          true,
          false,
          3000 
        );
      }

      if (result.error) {
        configureAlert(
          `Failed to ${editingUom ? "update" : "add"} UOM: ${result.error}`,
          closeAlert,
          closeAlert,
          "OK",
          "",
          true,
          false,
          3000 
        );
      } else {
        setEditingUom(null);
        setOriginalUom(null);
        setNewUom({ code: "", name: "", status: "ACTIVE" });
        setValidationErrors({});
      }
    } catch (error) {
      console.error("Error saving UOM:", error);
      configureAlert(
        `Failed to ${editingUom ? "update" : "add"} UOM.`,
        closeAlert,
        closeAlert,
        "OK",
        "",
        true,
        false,
        3000 
      );
    }
    
    setIsLoading(false);
  };

  const handleDeleteUom = async (id: string) => {
    const uomToDelete = uomData.find(uom => uom._id === id);
    
    if (uomToDelete) {
      configureAlert(
        `Are you sure you want to Inactive ${uomToDelete.name}?`,
        async () => {
          closeAlert(); 
          
          setIsLoading(true);
          try {
            const result = await deleteUom(id);
            
            if (!result.error) {
              configureAlert(
                `UOM has been Inactive successfully.`,
                () => {
                  loadUoms(); 
                  closeAlert(); 
                },
                closeAlert,
                "OK",
                "",
                true,
                false,
                3000 
              );
            } else {
              configureAlert(
                `Failed to Inactive UOM: ${result.error}`,
                closeAlert,
                closeAlert,
                "OK",
                "",
                true,
                false,
                3000 
              );
            }
          } catch (error) {
            console.error("Error deleting UOM:", error);
            configureAlert(
              "Failed to Inactive UOM.",
              closeAlert,
              closeAlert,
              "OK",
              "",
              true,
              false,
              3000 
            );
          }
          setIsLoading(false);
        },
        closeAlert,
        "Yes",
        "No",
        true,
        true,
        null 
      );
    }
  };

  const handleModalClose = () => {
    const currentUom = editingUom || newUom;
    if (isModalOpen && (editingUom || currentUom.code.trim() !== "" || currentUom.name.trim() !== "")) {
      configureAlert(
        "Are you sure you want to close without saving?",
        () => {
          setIsModalOpen(false);
          setEditingUom(null);
          setOriginalUom(null);
          setNewUom({ code: "", name: "", status: "ACTIVE" });
          setValidationErrors({});
          closeAlert();
        },
        closeAlert,
        "Yes",
        "No",
        true,
        true,
        null 
      );
    } else {
      setIsModalOpen(false);
      setEditingUom(null);
      setOriginalUom(null);
      setNewUom({ code: "", name: "", status: "ACTIVE" });
      setValidationErrors({});
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    const hasNumeric = /\d/.test(value);
    
    if (hasNumeric) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: "Numeric characters are not allowed",
      }));
      return;
    }
    
    if (editingUom) {
      setEditingUom({ ...editingUom, [name]: value });
    } else {
      setNewUom({ ...newUom, [name]: value });
    }
    
    if (validationErrors[name as keyof typeof validationErrors]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleSelectChange = (selectedOption: any, { name }: any) => {
    const value = selectedOption?.value || "";
  
    if (editingUom) {
      setEditingUom((prev) => ({
        ...prev!,
        [name]: value,
      }));
    } else {
      setNewUom((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
    
    if (validationErrors[name as keyof typeof validationErrors]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const columns = [
    { key: "code", label: "Code", sortable: true },
    { key: "name", label: "Name", sortable: true },
    { key: "status", label: "Status", sortable: true }
  ];

  const statusOptions = [
    { value: 'ACTIVE', label: 'ACTIVE' },
    { value: 'INACTIVE', label: 'INACTIVE' }
  ];

  return (
    <div className="item-models-container">
    
      <style>{customStyles}</style>
      
      <div className="item-models-header">
        <div className="border">
          <div className="start-end-layout">
            <div className="breadcrumb-nav">
              <span>General</span>
              <span>Uom Master</span>
            </div>
            {permission.menus.uoms.create ?
            <div className="items-button-container tooltip-wrapper">
              <button 
                className="item-models-add-btn" 
                onClick={() => {
                  setEditingUom(null);
                  setOriginalUom(null);
                  setNewUom({ code: "", name: "", status: "ACTIVE" });
                  setValidationErrors({});
                  setIsModalOpen(true);
                }} 
                disabled={isLoading}
              >
                <Plus size={16} />
              </button>
            </div>
            :<></>}
          </div>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {isLoading ? (
        <div className="spinner"></div>
      ) : (
        <div className="box-container">
          <TableComponent
            data={uomData}
            columns={columns}
            onEdit={(u: UOM) => { 
              setEditingUom({...u});
              setOriginalUom({...u});
              setValidationErrors({});
              setIsModalOpen(true); 
            }}
            onDelete={(id: string) => handleDeleteUom(id)}
            setFilter={(filters: Record<string, any>) => {
              Object.entries(filters).forEach(([key, value]) => {
                dispatch(updateFilters({ key, value: String(value) }));
              });
            }}
            setSorting={handleSortChange}
            defaultSortConfig={{ key: sortBy, direction: sortOrder }}
          />

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            totalItems={totalItems}
          />
        </div>
      )}

      {isModalOpen && (
        <div className="item-models-modal-overlay">
          <div className="item-models-modal">
            <div className="item-models-modal-header">
              <h5>{editingUom ? "Edit UOM" : "Add New UOM"}</h5>
              <button className="item-models-modal-close" onClick={handleModalClose}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleAddOrEdit} noValidate>
              <div className="form-group">
                <input
                  type="text"
                  name="code"
                  placeholder="Code"
                  value={editingUom ? editingUom.code : newUom.code}
                  onChange={handleInputChange}
                  className={validationErrors.code ? "input-error" : ""}
                  required
                />
                {validationErrors.code && (
                  <div className="form-error-message">{validationErrors.code}</div>
                )}
              </div>

              <div className="form-group">
                <input
                  type="text"
                  name="name"
                  placeholder="Name"
                  value={editingUom ? editingUom.name : newUom.name}
                  onChange={handleInputChange}
                  className={validationErrors.name ? "input-error" : ""}
                  required
                />
                {validationErrors.name && (
                  <div className="form-error-message">{validationErrors.name}</div>
                )}
              </div>

              {editingUom && (
                <div className="select-container">
                  <Select
                    name="status"
                    options={statusOptions}
                    value={statusOptions.find(option => 
                      option.value === (editingUom ? editingUom.status : newUom.status)
                    )}
                    onChange={handleSelectChange}
                    placeholder="Select Status"
                    className={validationErrors.status ? "select-error" : ""}
                    classNamePrefix="react-select"
                  />
                  {validationErrors.status && (
                    <div className="form-error-message">{validationErrors.status}</div>
                  )}
                </div>
              )}

              <button type="submit" disabled={isLoading} className="submit-button">
                {isLoading ? "Saving..." : editingUom ? "Update" : "Add"}
              </button>
            </form>
          </div>
        </div>
      )}

 
      {showAlert && (
        <CustomAlert 
          message={alertMessage} 
          onConfirm={alertConfirmAction} 
          onCancel={alertCancelAction}
          confirmText={confirmButtonText}
          cancelText={cancelButtonText}
          showConfirmButton={showConfirmButton}
          showCancelButton={showCancelButton}
          autoCloseDelay={autoCloseDelay}
        />
      )}
    </div>
  );
};

export default UOMMaster;